import { useState, useEffect } from 'react'
import { updateApplicationSyncPolicy } from '@/lib/api'
import { invalidateApplicationReadModels } from '@/lib/connect-query-invalidation'
import { useMutationWithToast } from '@/hooks/useMutationWithToast'
import type { Application as ProtoApplication, SyncPolicy as ProtoSyncPolicy } from '@/lib/generated-types'

type Application = Pick<ProtoApplication, 'syncPolicy'>
type SyncPolicy = Pick<ProtoSyncPolicy, 'autoSync' | 'selfHeal' | 'pruneResources'>

export function useSyncPolicy(application: Application | undefined, appId: string) {
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false)
  const [selfHealEnabled, setSelfHealEnabled] = useState(false)
  const [pruneResourcesEnabled, setPruneResourcesEnabled] = useState(false)

  useEffect(() => {
    if (application?.syncPolicy) {
      setAutoSyncEnabled(application.syncPolicy.autoSync || false)
      setSelfHealEnabled(application.syncPolicy.selfHeal || false)
      setPruneResourcesEnabled(application.syncPolicy.pruneResources || false)
    }
  }, [application?.syncPolicy])

  const updateSyncPolicyMutation = useMutationWithToast(updateApplicationSyncPolicy, {
    successTitle: 'Sync policy updated',
    successMessage: 'Application sync policy has been updated successfully.',
    errorTitle: 'Failed to update sync policy',
    invalidate: invalidateApplicationReadModels,
    onError: () => {
      if (application?.syncPolicy) {
        setAutoSyncEnabled(application.syncPolicy.autoSync || false)
        setSelfHealEnabled(application.syncPolicy.selfHeal || false)
        setPruneResourcesEnabled(application.syncPolicy.pruneResources || false)
      }
    },
  })

  const updateSyncPolicy = (nextPolicy: SyncPolicy) => {
    setAutoSyncEnabled(nextPolicy.autoSync)
    setSelfHealEnabled(nextPolicy.selfHeal)
    setPruneResourcesEnabled(nextPolicy.pruneResources)
    updateSyncPolicyMutation.mutate({
      id: appId,
      syncPolicy: nextPolicy,
    })
  }

  return {
    syncPolicy: {
      autoSync: autoSyncEnabled,
      selfHeal: selfHealEnabled,
      pruneResources: pruneResourcesEnabled,
    },
    updateSyncPolicy,
    isPending: updateSyncPolicyMutation.isPending,
  }
}
